import React from 'react'
import { format, fromUnixTime } from 'date-fns'
import { BusinessPhase, SprintPacketHydrated } from '@marketing-milk/interfaces'
import { SurfacePalette, type } from '../../tokens'

interface Props {
  palette: SurfacePalette
  packet: SprintPacketHydrated
  businessName: string | undefined
  phase: BusinessPhase | undefined
}

const PHASE_LABEL: Record<BusinessPhase, string> = {
  [BusinessPhase.CRAWL]: 'Crawl',
  [BusinessPhase.WALK]: 'Walk',
  [BusinessPhase.RUN]: 'Run',
  [BusinessPhase.FLY]: 'Fly',
}

const Meta = ({
  palette,
  label,
  value,
}: {
  palette: SurfacePalette
  label: string
  value: string
}) => (
  <div>
    <div
      style={{
        fontFamily: type.family.mono,
        fontSize: type.size.eyebrow,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: palette.textFaint,
        marginBottom: 6,
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: type.family.sans, fontSize: type.size.bodyLg, color: palette.text }}>
      {value}
    </div>
  </div>
)

export const PresentCover = ({ palette, packet, businessName, phase }: Props) => {
  const start = fromUnixTime(packet.sprint.startTime)
  const end = fromUnixTime(packet.sprint.endTime)
  const dateRange = `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`
  const phaseLabel = phase !== undefined ? PHASE_LABEL[phase] : undefined

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: palette.text,
      }}
    >
      <div
        style={{
          fontFamily: type.family.mono,
          fontSize: type.size.eyebrow,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: palette.accent,
          marginBottom: 20,
        }}
      >
        Sprint Packet
      </div>
      <h1
        style={{
          margin: 0,
          fontFamily: type.family.serif,
          fontSize: type.size.hero,
          fontWeight: type.weight.regular,
          lineHeight: 1.02,
        }}
      >
        {businessName ?? '—'}
      </h1>
      <div
        style={{
          marginTop: 12,
          fontFamily: type.family.serif,
          fontSize: type.size.h1,
          fontStyle: 'italic',
          color: palette.accent,
        }}
      >
        {packet.sprint.name}
      </div>

      <div
        style={{
          marginTop: 48,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px 64px',
        }}
      >
        <Meta palette={palette} label="Sprint window" value={dateRange} />
        {phaseLabel && <Meta palette={palette} label="ROI phase" value={phaseLabel} />}
      </div>
    </div>
  )
}
